import React, { useEffect, useRef } from 'react';
import type { ChangeEvent, ReactElement } from 'react';
import { debounce } from 'lodash';
import { useController } from 'react-hook-form';
import type { Control, FieldPath, FieldValues } from 'react-hook-form';
import { defaultDebounceMilliseconds } from './helpers';

type TextareaProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
> = {
  id: string;
  control: Control<TFieldValues>;
  name: TName;
  disabled?: boolean;
  onChange?: (e: ChangeEvent<HTMLTextAreaElement>) => void;
  debounceOnChange?: boolean; // Determines if the onChange event should be debounced.
  debounceMilliseconds?: number; // The number of milliseconds to debounce the onChange event.
  rows?: number;
};

export default function Textarea<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
>({
  id,
  control,
  name,
  disabled,
  onChange,
  debounceOnChange,
  debounceMilliseconds = defaultDebounceMilliseconds,
  rows = 3
}: TextareaProps<TFieldValues, TName>): ReactElement {
  const { field } = useController({ control, name });

  const onChangeDebounced = useRef(
    debounce((e: ChangeEvent<HTMLTextAreaElement>) => {
      if (onChange) {
        onChange(e);
      }
    }, debounceMilliseconds)
  ).current;
  useEffect(() => () => onChangeDebounced.cancel(), [onChangeDebounced]);

  return (
    <textarea
      {...field}
      id={id}
      className="form-control"
      rows={rows}
      disabled={disabled}
      onChange={(e: ChangeEvent<HTMLTextAreaElement>): void => {
        field.onChange(e);
        if (onChange) {
          if (debounceOnChange) {
            onChangeDebounced(e);
          } else {
            onChange(e);
          }
        }
      }}
    />
  );
}
